<script setup lang="ts">
import { ref, computed } from "vue";
import BucketUnits from "@/modules/dimension_values/components/BucketUnits.vue";
import DimensionDO = App.Data.DimensionDO;
import DimensionUnit = App.Enums.DimensionUnits;
import BucketPutDTO = App.Data.BucketPutDTO;
import Modal from "@/ui/components/modal.vue";

const loading = defineModel<boolean>("loading");
const emit = defineEmits<{
	(e: "batchAddValues", itemCount: number, itemRange: number): void;
	(e: "toggleAutoLabel");
	(e: "toggleInvertedSortOrder");
	(e: "randomizeColors");
	(e: "setAsDefault");
	(e: "delete");
}>();
const props = defineProps<{
	dimension: DimensionDO;
	bucket: BucketPutDTO;
}>();

const addValuesDialog = ref(false);

const newValueCount = ref(5);
const valueGap = ref(1);

const settings = computed(() => {
	return [
		{
			title: "Add values",
			icon: "mdi-plus-box-multiple",
			toolTip: "Add multiple values to bucket",
			callback: () => (addValuesDialog.value = true),
		},
		{
			title:
				(props.bucket.bucket.should_auto_label ? "Disable" : "Enable") +
				" auto-label",
			icon: "mdi-label-multiple",
			toolTip:
				"Auto-generate labels from start values. Overrides existing labels",
			callback: () => emit("toggleAutoLabel"),
		},
		{
			title:
				(props.bucket.bucket.has_inverted_sort_order ? "Disable" : "Enable") +
				" inverted sort order",
			icon: "mdi-swap-vertical-bold",
			toolTip: "Reverse the order of the values in the explorer",
			callback: () => emit("toggleInvertedSortOrder"),
		},
		{
			title: "Randomize colors",
			icon: "mdi-palette",
			callback: () => emit("randomizeColors"),
		},
		{
			title: "Make default",
			icon: "mdi-star",
			toolTip: "Set as default bucket for this dimension in the explorer",
			callback: () => emit("setAsDefault"),
		},
		{
			title: "Delete",
			icon: "mdi-delete",
			callback: () => emit("delete"),
		},
	];
});
</script>

<template>
	<v-menu>
		<template v-slot:activator="{ props }">
			<v-btn
				:disabled="loading"
				rounded="rounded"
				height="100%"
				color="secondary"
				icon="mdi-dots-vertical"
				v-bind="props"
			/>
		</template>
		<v-list>
			<v-tooltip
				v-for="(setting, i) in settings"
				:key="i"
				open-delay="300"
				:disabled="!setting.toolTip"
				location="left"
			>
				<template #activator="{ props }">
					<v-list-item v-bind="props" :value="i" @click="setting.callback()">
						<template #prepend>
							<v-icon class="mr-n5" :icon="setting.icon"></v-icon>
						</template>
						<v-list-item-title>
							{{ setting.title }}
						</v-list-item-title>
					</v-list-item>
				</template>
				<span>{{ setting.toolTip }}</span>
			</v-tooltip>
		</v-list>
	</v-menu>

	<modal
		v-model:visible="addValuesDialog"
		title="Add Values"
		icon="fa-solid fa-plus"
		width="600px"
		@submit="emit('batchAddValues', newValueCount, valueGap)"
	>
		<v-alert
			v-if="newValueCount >= 100"
			title="Large buckets may slow down queries"
			type="warning"
			color="orange"
			variant="tonal"
			density="comfortable"
		/>
		<v-number-input
			v-model="newValueCount"
			label="Amount of values"
			:min="2"
			:max="1_000"
			control-variant="stacked"
			inset
		/>

		<v-number-input
			v-if="props.dimension.unit === 'default'"
			v-model="valueGap"
			label="Gap between values"
			:min="1"
			:max="10_000_000"
			control-variant="stacked"
			inset
		/>
		<v-card
			v-else
			height="48"
			variant="flat"
			density="compact"
			class="d-flex flex-column bucket-unit-card"
		>
			<span> Gap between values </span>
			<bucket-units
				v-model:unit="dimension.unit as DimensionUnit"
				v-model:value="valueGap"
				:min="1"
			/>
		</v-card>
	</modal>
</template>
